// Sentry initialization should be imported first for early error capture

import { ClerkProvider, useAuth, useUser } from "@clerk/clerk-react";
import { enUS, ptBR } from "@clerk/localizations";
import { configureApiClient } from "@packages/api-client/client-config";
import { onlineManager, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import { StrictMode, useEffect, useReducer, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import { LoadingSpinner } from "./components/LoadingSpinner.tsx";
import { OfflineUnavailable } from "./components/OfflineUnavailable.tsx";
import { InstallCard } from "./components/pwa/InstallCard.tsx";
import { ServiceWorkerAutoUpdate } from "./components/pwa/ServiceWorkerAutoUpdate.tsx";
import { SplashLoadingScreen } from "./components/pwa/SplashLoadingScreen.tsx";
import { RouteErrorPanel } from "./components/RouteErrorPanel.tsx";
import { DetailSkeleton } from "./components/ui/skeleton.tsx";
import {
	type OfflineBootState,
	OfflineSessionContext,
	type OfflineSessionContextValue,
} from "./context/OfflineSessionContext.tsx";
import { SyncStatusProvider } from "./context/SyncStatusContext.tsx";
import { env } from "./env.ts";
import { useClerkSessionPersister } from "./hooks/useClerkSessionPersister.ts";
import { LanguageProvider, useLanguage } from "./hooks/useLanguage.tsx";
import { useOnlineStatus } from "./hooks/useOnlineStatus.ts";
import { ThemeProvider } from "./hooks/useTheme.tsx";
import { SignUpAnalytics } from "./hooks/useTrackCompletedSignUp.ts";
import i18n, { DEFAULT_LANGUAGE, type LanguageKey } from "./i18n/index.ts";
import enTranslations from "./i18n/locales/en_US.ts";
import ptTranslations from "./i18n/locales/pt_BR.ts";
import { createMutationCache } from "./lib/mutationToasts.ts";
import { shouldDehydrateQuery } from "./lib/offline/index.ts";
import {
	initReachability,
	getSnapshot as reachabilitySnapshot,
	subscribe as reachabilitySubscribe,
} from "./lib/offline/reachability.ts";
import { OfflineUnavailableError } from "./lib/offline/routeGuard.ts";
import { setQueueOwner } from "./lib/offlineQueue.ts";
import {
	getOfflineSession,
	type OfflineSession,
} from "./lib/offlineSession.ts";
import { requestAppReload } from "./lib/pwa/appReload.ts";
import { initServiceWorkerClient } from "./lib/pwa/serviceWorkerClient.ts";
import { idbPersister } from "./lib/queryCache.ts";
import { APP_QUERY_DEFAULTS } from "./lib/queryDefaults.ts";
import { reportApiRequestError } from "./lib/sentryApiSanitizer.ts";
import reportWebVitals from "./reportWebVitals.ts";
import { routeTree } from "./routeTree.gen.ts";
import { initSentry, Sentry, setSentryUser } from "./sentry.ts";
import "./styles.css";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
	throw new Error("Add your Clerk Publishable Key to the .env.local file");
}

// Boot real reachability detection and keep TanStack Query's onlineManager in sync.
// This replaces the browser's navigator.onLine with a genuine probe so queries
// pause on captive portals and network outages, not just cable-unplugged events.
// The probe targets our own API: any HTTP response from it (including the
// 400/401 this unauthenticated HEAD earns) proves the server we actually depend
// on is reachable. A third party's availability is not our connectivity — the
// previous gstatic target reported the app offline whenever an ad-blocker, DNS
// hiccup, or corporate filter got in the way of Google.
initReachability(`${env.VITE_BACKEND_API_URL}/internal/health`);
onlineManager.setOnline(navigator.onLine); // synchronous initial state before first probe
reachabilitySubscribe(() => {
	onlineManager.setOnline(reachabilitySnapshot());
});

const mutationCache = createMutationCache(i18n);

// Phase 5 — Data schema version for persisted cache buster.
// Deliberately separate from VITE_APP_VERSION so a new deploy doesn't wipe
// prepared offline datasets. Bump this constant only when the persisted data
// schema changes in a backward-incompatible way (spec AC-F5).
const DATA_SCHEMA_VERSION = "1";

const queryClient = new QueryClient({
	mutationCache,
	defaultOptions: {
		queries: APP_QUERY_DEFAULTS,
		mutations: {
			retry: false,
		},
	},
});

// After a deploy, hashed chunk URLs in the user's old `index.html` no longer
// exist; CloudFront/S3 returns the SPA fallback HTML and the browser refuses
// to import it as JS — surfacing as a MIME-type TypeError or ChunkLoadError.
// `defaultOnCatch` catches that and reloads to pick up the new build.
// The time-based guard avoids an infinite reload loop if the new build itself
// is genuinely broken: we only auto-reload once per 10s window.
const CHUNK_RELOAD_GUARD_KEY = "ruralops:lastChunkReload";
const CHUNK_RELOAD_GUARD_WINDOW_MS = 10_000;

// Create a new router instance
const router = createRouter({
	routeTree,
	context: {
		queryClient,
		// biome-ignore lint/style/noNonNullAssertion: reference from docs https://tanstack.com/router/latest/docs/framework/react/guide/authenticated-routes
		get_token: undefined!,
	},
	defaultPreload: false,
	// Offline-gated routes throw OfflineUnavailableError from beforeLoad;
	// render the inline unavailable panel in the matched route's slot so the
	// nav/shell stays intact. Every other error renders the in-shell
	// RouteErrorPanel — an honest "couldn't load" with retry — instead of a
	// blank slot or a dev-styled stack trace. Failure must never look like
	// emptiness on a system of record.
	defaultErrorComponent: ({ error, reset }) =>
		error instanceof OfflineUnavailableError ? (
			<OfflineUnavailable variant={error.stance} />
		) : (
			<RouteErrorPanel error={error} reset={reset} />
		),
	// Routes whose loaders await data render a skeleton in the route slot
	// (after the default pending threshold) instead of a blank page.
	defaultPendingComponent: DetailSkeleton,
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
	defaultOnCatch: (error) => {
		const msg = (error as Error)?.message ?? "";
		const name = (error as Error)?.name ?? "";
		const isChunkLoadError =
			error instanceof TypeError &&
			/MIME type|Failed to fetch dynamically imported module/i.test(msg);
		const isChunkLoadName = name === "ChunkLoadError";
		if (!isChunkLoadError && !isChunkLoadName) return;

		const last = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) ?? "0");
		if (Date.now() - last <= CHUNK_RELOAD_GUARD_WINDOW_MS) return;
		sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
		// Routed through the single reload arbiter so queued offline work is
		// flushed to IndexedDB before the page goes away.
		void requestAppReload("chunk-missing");
	},
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

// Initialize Sentry with router for navigation tracking
initSentry(router);

// Map language keys to Clerk localizations
const clerkLocalizationMap: Record<LanguageKey, typeof enUS | typeof ptBR> = {
	en_US: {
		...enUS,
		signIn: {
			...enUS.signIn,
			start: { ...enUS.signIn?.start, ...enTranslations.auth.signIn },
		},
	},
	pt_BR: {
		...ptBR,
		signIn: {
			...ptBR.signIn,
			start: { ...ptBR.signIn?.start, ...ptTranslations.auth.signIn },
		},
	},
};

/**
 * Component to sync Clerk user context to Sentry for error attribution.
 */
function SentryUserSync() {
	const { user, isLoaded } = useUser();

	useEffect(() => {
		if (isLoaded) {
			if (user) {
				// Opaque id only — no email/name to Sentry (LGPD).
				setSentryUser({
					id: user.id,
				});
			} else {
				setSentryUser(null);
			}
		}
	}, [isLoaded, user]);

	return null;
}

/**
 * Persists the Clerk session to IDB while signed-in.
 * Only mounted when isLoaded is true so hooks are stable.
 */
function SessionPersisterMount() {
	useClerkSessionPersister();
	return null;
}

/**
 * Boot-state machine — encodes every valid transition explicitly so we never
 * fetch the IDB session more than once (was a useRef guard), and every
 * transition is a named action instead of a scatter of setState calls.
 *
 * Transitions:
 *   loading → online       (Clerk finished loading online)
 *   loading → bypass       (Clerk still loading, offline session present)
 *   loading → expired      (Clerk still loading, session existed but TTL elapsed)
 *   loading → first-use    (Clerk still loading, no session record found)
 *   bypass/expired/first-use → online   (Clerk loads while offline path is showing)
 */
type BootStateSnapshot = {
	bootState: OfflineBootState;
	offlineSession: OfflineSession | null;
	hasResolvedOffline: boolean;
};

type BootAction =
	| { type: "clerk-loaded" }
	| {
			type: "offline-resolved";
			result: { session: OfflineSession | null; expired: boolean };
	  };

function bootReducer(
	state: BootStateSnapshot,
	action: BootAction,
): BootStateSnapshot {
	switch (action.type) {
		case "clerk-loaded":
			// Transition back to the normal Clerk-owned flow. Session is dropped
			// because we no longer need the IDB-cached identity — Clerk is source
			// of truth again.
			return {
				bootState: "online",
				offlineSession: null,
				hasResolvedOffline: state.hasResolvedOffline,
			};
		case "offline-resolved": {
			const { session, expired } = action.result;
			if (session) {
				return {
					bootState: "bypass",
					offlineSession: session,
					hasResolvedOffline: true,
				};
			}
			return {
				bootState: expired ? "expired" : "first-use",
				offlineSession: null,
				hasResolvedOffline: true,
			};
		}
	}
}

function useBootState(): {
	bootState: OfflineBootState;
	offlineSession: OfflineSession | null;
} {
	const { isLoaded } = useAuth();
	const online = useOnlineStatus();

	const [state, dispatch] = useReducer(bootReducer, {
		bootState: isLoaded ? "online" : "loading",
		offlineSession: null,
		hasResolvedOffline: false,
	});

	useEffect(() => {
		if (isLoaded) {
			if (state.bootState !== "online") dispatch({ type: "clerk-loaded" });
			return;
		}
		// Clerk still loading and we're online — keep showing loading.
		if (online) return;
		// Offline branch: resolve the IDB session at most once.
		if (state.hasResolvedOffline) return;
		getOfflineSession().then((result) =>
			dispatch({ type: "offline-resolved", result }),
		);
	}, [isLoaded, online, state.bootState, state.hasResolvedOffline]);

	return { bootState: state.bootState, offlineSession: state.offlineSession };
}

function SetupQueryClient() {
	const { getToken, isLoaded, userId } = useAuth();
	const configuredRef = useRef(false);
	const { bootState, offlineSession } = useBootState();
	const queueOwnerId = userId ?? offlineSession?.userId ?? null;
	useEffect(() => setQueueOwner(queueOwnerId), [queueOwnerId]);
	// Gate the RouterProvider behind IDB cache restoration. Without this,
	// route loaders fire before PersistQueryClientProvider has hydrated the
	// cache from IDB, so ensureQueryData finds nothing and hangs (offline,
	// networkMode:offlineFirst, paused retry). onSuccess fires once after the
	// first restore attempt — even when IDB is empty — so online boot is also safe.
	const [cacheRestored, setCacheRestored] = useState(false);

	// Wire the API client synchronously during the FIRST render after Clerk has
	// loaded — strictly before the RouterProvider mounts and route loaders fire.
	// Calling this in a useEffect (post-commit) created a race: loaders kicked
	// off queries that hit an unconfigured client and got stuck in `paused`
	// state, leaving the org parent route suspended forever. Side effects in
	// render are normally a smell; `configureApiClient` is idempotent and the
	// ref guard makes it strictly-once, so the tradeoff is worth it here.
	// In bypass mode (offline boot with cached session), Clerk is not loaded so
	// `isLoaded` is false and `getToken` returns null. We still configure the
	// API client with the correct baseURL so that requests go to the right origin
	// (where the Workbox cache has entries). Unauthenticated requests are served
	// from the Workbox StaleWhileRevalidate cache during the transition period.
	const shouldConfigure =
		(isLoaded || bootState === "bypass") && !configuredRef.current;
	if (shouldConfigure) {
		configuredRef.current = true;
		configureApiClient({
			baseURL: env.VITE_BACKEND_API_URL,
			getToken: isLoaded ? getToken : async () => null,
			onResponse: (response) => {
				Sentry.addBreadcrumb({
					category: "api",
					message: `${response.config.method?.toUpperCase()} ${response.config.url}`,
					level: "info",
					data: { status: response.status },
				});
			},
			onRequestError: (error: AxiosError) => reportApiRequestError(error),
		});
	}

	// Show splash screens for non-bypass offline states.
	if (bootState === "loading") {
		return (
			<div className="min-h-screen bg-base-100 flex items-center justify-center">
				<LoadingSpinner size="lg" />
			</div>
		);
	}

	if (bootState === "expired") {
		return <SplashLoadingScreen state="expired" />;
	}

	if (bootState === "first-use") {
		return <SplashLoadingScreen state="first-use" />;
	}

	// Both "online" (normal Clerk flow) and "bypass" (offline with cached session)
	// render the full app via PersistQueryClientProvider.
	const offlineCtxValue: OfflineSessionContextValue = {
		bootState,
		offlineSession,
	};

	return (
		<OfflineSessionContext.Provider value={offlineCtxValue}>
			<PersistQueryClientProvider
				client={queryClient}
				persistOptions={{
					persister: idbPersister,
					// 5 days — aligns with the offline session window (spec §4).
					// Uses DATA_SCHEMA_VERSION (not VITE_APP_VERSION) so new builds
					// don't wipe prepared animal datasets (spec AC-F5, Phase 5).
					maxAge: 5 * 24 * 60 * 60 * 1000,
					buster: DATA_SCHEMA_VERSION,
					dehydrateOptions: {
						// Cast to any to bridge a minor @tanstack/query-core version
						// mismatch between react-query and react-query-persist-client
						// (5.100.14 vs 5.101.0). The runtime behavior is identical.
						// biome-ignore lint/suspicious/noExplicitAny: version mismatch cast
						shouldDehydrateQuery: shouldDehydrateQuery as any,
					},
				}}
				onSuccess={() => setCacheRestored(true)}
			>
				<SentryUserSync />
				{/* Persist session while signed-in so offline bypass works on next boot */}
				{bootState === "online" && isLoaded && <SessionPersisterMount />}
				{cacheRestored ? (
					<SyncStatusProvider>
						<RouterProvider
							router={router}
							context={{
								get_token: getToken,
							}}
						/>
					</SyncStatusProvider>
				) : (
					<div className="min-h-screen bg-base-100 flex items-center justify-center">
						<LoadingSpinner size="lg" />
					</div>
				)}
				<Toaster
					position="top-right"
					expand={true}
					richColors={true}
					closeButton={true}
					duration={4000}
					offset="4.5rem"
					toastOptions={{
						className:
							"!bg-base-100 !text-base-content !border-base-300 md:!mt-0",
						style: {
							backgroundColor: "hsl(var(--b1))",
							color: "hsl(var(--bc))",
							border: "1px solid hsl(var(--b3))",
						},
					}}
				/>
				<InstallCard />
			</PersistQueryClientProvider>
		</OfflineSessionContext.Provider>
	);
}

// ClerkProvider wrapper that uses dynamic localization based on selected language
function ClerkProviderWithLocalization({
	children,
}: {
	children: React.ReactNode;
}) {
	const { currentLanguage } = useLanguage();
	const clerkLocalization =
		clerkLocalizationMap[currentLanguage] ??
		clerkLocalizationMap[DEFAULT_LANGUAGE];

	return (
		<ClerkProvider
			localization={clerkLocalization}
			publishableKey={PUBLISHABLE_KEY}
			afterSignOutUrl="/"
			signInUrl="/sign-in"
			signUpUrl="/sign-up"
			signInFallbackRedirectUrl="/app/select-organization"
			signUpFallbackRedirectUrl="/app/select-organization"
		>
			<SignUpAnalytics />
			{children}
		</ClerkProvider>
	);
}

// Own the service-worker registration outside React and before the first
// render: `<StrictMode>` double-invokes every mount, so a registration created
// inside a component would produce two `Workbox` instances and two of every
// lifecycle listener. This is also the composition root for the update
// triggers and the safe-apply policy.
initServiceWorkerClient();

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	// React 19 error hooks for automatic Sentry error capture
	const root = ReactDOM.createRoot(rootElement, {
		// Called when an error is thrown and not caught by an ErrorBoundary
		onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
			console.error("Uncaught error:", error, errorInfo.componentStack);
		}),
		// Called when React catches an error in an ErrorBoundary
		onCaughtError: Sentry.reactErrorHandler(),
		// Called when React automatically recovers from errors
		onRecoverableError: Sentry.reactErrorHandler(),
	});

	root.render(
		<StrictMode>
			<LanguageProvider>
				<ClerkProviderWithLocalization>
					<ThemeProvider>
						{/* Above `SetupQueryClient` on purpose: its `bootState`
						    early returns (`loading` / `expired` / `first-use`)
						    render a splash and nothing else, so a mount inside
						    it never sees those states — exactly the long-lived
						    offline client that most needs the new build. Safe
						    here because its inputs are module-level external
						    stores, never `useSyncStatus()`, which throws
						    outside the provider nested below. */}
						<ServiceWorkerAutoUpdate />
						<SetupQueryClient />
					</ThemeProvider>
				</ClerkProviderWithLocalization>
			</LanguageProvider>
		</StrictMode>,
	);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
